// Turns the backend's free-form `preview` object into the diff rows the confirmation modal renders.
//
// `preview.current` / `preview.requested` are `Record<string, unknown>` on the wire (the shape
// depends on the action type), so the diff is derived from the union of their keys rather than a
// hardcoded field list — a new action type renders without a change here.
import type {
  PlatformActionDiffRow,
  PlatformActionGuardrails,
  PlatformActionPreview,
} from '../models/platformAction.types'
import type { ActionGuardrailsDto, ActionPreviewDto } from '../models/platformAction.wire'

/** The headline guardrail metric for a budget change. */
export const DELTA_PCT_METRIC = 'delta_pct'

function toRecord(value: Record<string, unknown> | null | undefined): Record<string, unknown> {
  return value && typeof value === 'object' ? value : {}
}

/** Metrics arrive as `Record<string, unknown>`; keep only the numeric ones so the UI can format. */
function adaptMetrics(metrics: Record<string, unknown> | null | undefined): Record<string, number> {
  const out: Record<string, number> = {}
  for (const [key, value] of Object.entries(toRecord(metrics))) {
    if (typeof value === 'number' && Number.isFinite(value)) out[key] = value
  }
  return out
}

export function adaptGuardrails(
  guardrails: ActionGuardrailsDto | null | undefined,
): PlatformActionGuardrails {
  const violations = Array.isArray(guardrails?.violations)
    ? guardrails.violations.filter((entry): entry is string => typeof entry === 'string')
    : []
  return { violations, metrics: adaptMetrics(guardrails?.metrics) }
}

/** Union of the two sides' keys, `current` first so the diff reads in the backend's own order. */
function diffRows(
  current: Record<string, unknown>,
  requested: Record<string, unknown>,
): PlatformActionDiffRow[] {
  const fields = [...new Set([...Object.keys(current), ...Object.keys(requested)])]
  return fields.map((field) => {
    const currentValue = current[field]
    const requestedValue = requested[field]
    return {
      field,
      current: currentValue,
      requested: requestedValue,
      // Structural compare: values are scalars in v1, but a nested payload must not read as equal
      // just because two object references differ.
      changed: JSON.stringify(currentValue) !== JSON.stringify(requestedValue),
    }
  })
}

export function adaptPreview(
  preview: ActionPreviewDto | null | undefined,
): PlatformActionPreview | null {
  if (!preview || typeof preview !== 'object') return null

  const guardrails = adaptGuardrails(preview.guardrails)
  const deltaPct = guardrails.metrics[DELTA_PCT_METRIC]

  return {
    source: typeof preview.source === 'string' && preview.source ? preview.source : null,
    rows: diffRows(toRecord(preview.current), toRecord(preview.requested)),
    guardrails,
    hasViolations: guardrails.violations.length > 0,
    deltaPct: deltaPct ?? null,
  }
}
